import { createContext, useContext } from 'react';

// How a structural edit reaches the stored within-generation order (TGCLOUD-1786
// Phase 2). The order lives in PedigreeChart (establishedOrder), two component
// layers above the menus that make structural edits — Set Relation, the edge
// menu's Remove Child / Remove Children / Remove Spouse, and Delete Person — and
// none of those layers passes it down. PedigreeChart provides one callback here
// instead: `pruneOrderForStructuralEdit(beforeMembers, afterMembers)`, to be
// called in the SAME event handler as the setMemberRecords that applies the edit,
// so React batches the two and the next layout never sees the new family with the
// old ranks (pedigree/structuralEditOrder.js explains why that matters).
//
// Outside a provider (component tests, any future host) it is a no-op, which is
// exactly the behaviour before this existed.
export const StructuralEditContext = createContext({
  pruneOrderForStructuralEdit: () => {}
});

export const usePruneOrderForStructuralEdit = () =>
  useContext(StructuralEditContext).pruneOrderForStructuralEdit;

/**
 * What PedigreeChart's pruneOrderForStructuralEdit does with ONE prune, pulled out
 * so it can be pinned without mounting the chart (there are no PedigreeChart
 * render tests). `prune(order)` is pruneRanksForStructuralEdit bound to the
 * edit's before/after members; it returns its argument when nothing is stale.
 *
 *  - The LIVE order: pruned through the state updater, so a no-op prune returns
 *    the same Map and React bails out (no relayout, no POST).
 *  - The UNDO HISTORY (TGCLOUD-1786 review): any armed checkpoint is resolved
 *    first so it is not folded into the user's previous drag, then every
 *    snapshot's order goes through the same prune. Undo is layout-only — it
 *    leaves the members as they are — so a snapshot still holding a pruned rank
 *    would put the wedge back, and the autosave would save it.
 *  - The STANDARD order while a saved VIEW is active: it waits in the stash, and
 *    nothing autosaves it (the live order routes to the view). A structural edit
 *    made inside a view used to prune the stash in memory only, so leaving
 *    without switching back left the Standard doc holding the stale rank, and
 *    every open starts on Standard. The pruned order is POSTed to the Standard doc
 *    as well. Saved views that are NOT active are not pruned (D5 prunes what is
 *    open; their orders are replayed as a lock only once applied).
 *
 * @param {object} args
 * @param {(order: Map) => Map} args.prune
 * @param {boolean} args.viewActive
 * @param {{establishedOrder?: Map}|null} args.stash
 * @param {(patch: object) => void} args.mergeStash
 * @param {(order: Map) => void} args.postStandardOrder
 * @param {{resolve: () => void, rewriteOrders: (fn: Function) => void}|null} args.history
 * @param {(updater: (prev: Map) => Map) => void} args.setEstablishedOrder
 */
export const applyStructuralEditPrune = ({
  prune,
  viewActive,
  stash,
  mergeStash,
  postStandardOrder,
  history,
  setEstablishedOrder
}) => {
  if (viewActive && stash?.establishedOrder instanceof Map) {
    const next = prune(stash.establishedOrder);
    if (next !== stash.establishedOrder) {
      mergeStash({ establishedOrder: next });
      postStandardOrder(next);
    }
  }
  if (history) {
    history.resolve();
    history.rewriteOrders(prune);
  }
  setEstablishedOrder((prev) => prune(prev));
};
